import { Check, Edit, OpenInNew, Workspaces } from "@mui/icons-material";
import { Box, Checkbox, Chip, FormControlLabel, IconButton, Skeleton, Stack, Typography } from "@mui/material";
import { type CustomJob, type ExternalJob, UserRole } from "@prisma/client";
import Cascader from "antd/lib/cascader";
import ConfigProvider from "antd/lib/config-provider";
import classNames from "classnames";
import React, { type ReactNode, useEffect, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import { useDebounce } from "use-debounce";
import { JobRequestModal } from "~/components/modals/JobRequestModal";
import { Button } from "~/components/ui/core/Button";
import { ConfirmationModal } from "~/components/ui/core/ConfirmationModal";
import { HighlightedText } from "~/components/ui/core/HighlightedText";
import { LegacyFormControl } from "~/components/ui/core/LegacyFormControl";
import { LegacyModal } from "~/components/ui/core/LegacyModal";
import { LegacyText } from "~/components/ui/core/LegacyText";
import { Link } from "~/components/ui/core/Link";
import { TextInput } from "~/components/ui/core/TextInput";
import { useApi } from "~/hooks/useApi";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { useForm } from "~/hooks/useForm";
import { useClassifyJobQuery, useListJobsQuery } from "~/hooks/useQueries";
import { useSession } from "~/hooks/useSession";
import { useI18n } from "~/lib/i18n/useI18n";
import { compact, isString, noop, omit, range, sumBy, takeRight } from "~/lib/lodash";
import { theme } from "~/lib/theme";
import { type ClassifyJobResponse } from "~/pages/api/classify-job";
import { type CreateCustomJobResponse, CreateCustomJobSchema } from "~/pages/api/create-custom-job";
import { useFetchJobFamiliesForSelectorQuery } from "~/pages/api/job/fetch-job-families-for-selector";
import { type UpdateCustomJobResponse, UpdateCustomJobSchema } from "~/pages/api/update-custom-job";
import { CUSTOM_JOB_PREFIX } from "~/services/custom-jobs/customJobPrefix";
import type { WeightedJob } from "~/services/job";

export const JOB_SELECTOR_LIMIT = 50;

// Make sure to keep in sync with the CSS selector in index.css
const BEST_MATCH_KEY = "best-match";

const LABEL_SEPARATOR = "\n";

const extractLabel = (label: string | undefined) => {
  if (!isString(label)) return label;

  const [name] = label.split(LABEL_SEPARATOR);

  return name;
};

type Props = {
  jobIds: string[];
  onChange: (jobIds: string[]) => void;
  multiple?: boolean;
  placeholder?: string;
  enableCustomJobs?: boolean;
  className?: string;
  disabled?: boolean;
  jobRequest?: {
    externalJob: Pick<ExternalJob, "id" | "name">;
  };
  suggestions?: ClassifyJobResponse["results"];
  size?: "small" | "large";
};

export const JobSelector: React.FC<Props> = ({
  jobIds,
  onChange,
  multiple = false,
  className,
  enableCustomJobs = false,
  placeholder,
  disabled = false,
  jobRequest,
  suggestions,
  size = "large",
}) => {
  const { t } = useI18n();
  const { user } = useSession();
  const { CAN_BYPASS_JOB_SELECTOR_LIMIT } = useFeatureFlags();
  const {
    data: jobFamilies,
    isFetching,
    refetch: refetchJobs,
  } = useFetchJobFamiliesForSelectorQuery({
    input: { enableCustomJobs },
  });
  const [open, isOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [openJobRequestModal, setOpenJobRequestModal] = useState(false);
  const [openCustomJobCreationModal, setOpenCustomJobCreationModal] = useState(false);
  const [selectedCustomJobForEdition, setSelectedCustomJobForEdition] = useState<string | null>(null);
  const [debouncedSearch] = useDebounce(search, 300);

  const { data: jobsSuggestionsData, isFetching: isClassifying } = useClassifyJobQuery({
    search: debouncedSearch,
  });
  const isLoading = isFetching || isClassifying;
  const dynamicPlaceholder = open ? t("components.ui.job-selector.open-placeholder") : placeholder;

  const flatJobs = useMemo(() => {
    if (!jobFamilies) return [];

    return jobFamilies
      .map((family) => family.jobs.map((job) => ({ ...job, familyId: `${family.id}`, family: omit(family, "jobs") })))
      .flat();
  }, [jobFamilies]);

  const values = useMemo(() => {
    const values = jobIds.map((jobId) => {
      const flatJob = flatJobs.find((flatJob) => flatJob.id === jobId);
      if (!flatJob) return;

      return [flatJob.family.id, flatJob.id];
    });

    return compact(values);
  }, [jobIds, jobFamilies]);

  const options = useMemo(() => {
    if (!jobFamilies) {
      return [];
    }

    const options = jobFamilies
      .map((jobFamily) => ({
        label: jobFamily.name,
        value: `${jobFamily.id}`,
        aliases: [],
        children: jobFamily.jobs.map((job) => ({
          label: [job.name, ...job.aliases].join(LABEL_SEPARATOR),
          value: `${job.id}`,
          aliases: job.aliases,
        })),
      }))
      .filter((option) => {
        return option.children.length > 0;
      });

    if (suggestions?.length) {
      options.unshift({
        label: t("components.ui.job-selector.suggestions"),
        value: "suggestions",
        aliases: [],
        children: suggestions.map((suggestion) => ({
          label: suggestion.job.name,
          value: `${suggestion.job.id}`,
          aliases: suggestion.companyExternalJobTitles ?? [],
        })),
      });
    }

    if (jobsSuggestionsData?.results.length) {
      options.unshift({
        label: search,
        value: BEST_MATCH_KEY,
        aliases: [],
        children: jobsSuggestionsData.results.map((suggestion) => ({
          label: suggestion.job.name,
          value: `${suggestion.job.id}`,
          aliases: suggestion.companyExternalJobTitles ?? [],
        })),
      });
    }

    return options;
  }, [jobFamilies, jobsSuggestionsData]);

  const filteredOptions = useMemo(() => {
    const lowerSearch = debouncedSearch.toLowerCase();

    return options
      .map((option) => {
        if (option.label.toLowerCase().includes(lowerSearch)) {
          return option;
        }

        return {
          ...option,
          children: option.children.filter((child) => child.label.toLowerCase().includes(lowerSearch)),
        };
      })
      .filter((option) => option.children.length > 0);
  }, [options, debouncedSearch]);

  const onJobChange = (option: string[] | null) => {
    onJobsChange(option ? [option] : []);
  };

  const onJobsChange = (options: string[][]) => {
    let newJobIds = compact(
      options
        .flatMap(([jobFamilyId, jobId]) =>
          flatJobs.filter((job) => {
            // When there's only one best match, no job id is provided so we have to manually fetch it from the matches
            if (jobFamilyId === BEST_MATCH_KEY && jobsSuggestionsData?.results.length === 1)
              return job.id === `${jobsSuggestionsData?.results[0]?.job.id}`;

            if (!jobId) return job.familyId === jobFamilyId;

            return job.id === jobId;
          })
        )
        .map((job) => job.id)
    );

    if (!CAN_BYPASS_JOB_SELECTOR_LIMIT) {
      newJobIds = takeRight(newJobIds, JOB_SELECTOR_LIMIT);
    }

    onChange(newJobIds);
    setSearch("");
  };

  const canAddJob = useMemo(() => {
    if (!multiple || CAN_BYPASS_JOB_SELECTOR_LIMIT) return true;

    return values.length < JOB_SELECTOR_LIMIT;
  }, [multiple, values]);

  const dropdownRender = (menu: ReactNode) => {
    return (
      <Stack className="z-[9999] rounded border">
        {!canAddJob && (
          <Box className="border-b bg-secondary-50 px-4 py-1">
            <Typography variant="caption">
              {t("components.ui.job-selector.max-jobs", { limit: JOB_SELECTOR_LIMIT })}
            </Typography>
          </Box>
        )}

        {!filteredOptions.length ? (
          <Box className="px-4 py-2">
            {isLoading ? (
              <Stack spacing={1}>
                {range(5).map((index) => (
                  <Skeleton key={index} height={20} width={`calc(100% - ${index * 20}px)`} />
                ))}
              </Stack>
            ) : (
              t("components.ui.job-selector.no-results")
            )}
          </Box>
        ) : (
          menu
        )}

        {jobRequest && (
          <Box className="border-t bg-gray-50 px-4 py-2">
            <a className="group" onClick={() => setOpenJobRequestModal(true)}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {t("components.ui.job-selector.cant-map-this-job")}
                </Typography>
                <Typography variant="caption" color="text.secondary" className="underline group-hover:text-gray-900">
                  {t("components.ui.job-selector.make-a-job-request")}
                </Typography>
              </Stack>
            </a>
          </Box>
        )}

        {user && enableCustomJobs && (
          <Box className="border-t bg-gray-50 px-4 py-2">
            <a className="group" onClick={() => setOpenCustomJobCreationModal(true)}>
              <Typography variant="caption" color="text.secondary">
                <Trans i18nKey="components.ui.job-selector.create-your-own-custom-jobs">
                  <span className="cursor-pointer underline group-hover:text-gray-900" />
                </Trans>
              </Typography>
            </a>
          </Box>
        )}

        <Box className="border-t bg-gray-50 px-4 py-2">
          <Link to="/jobs" newTab noLinkStyle className="group">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" className="group-hover:text-gray-900">
                {t("components.ui.job-selector.learn-about-figures-jobs")}
              </Typography>
              <OpenInNew className="text-sm text-gray-500 group-hover:text-gray-900" />
            </Stack>
          </Link>
        </Box>
      </Stack>
    );
  };

  const displayRender = (labels: string[]) => {
    return extractLabel(labels[1]) ?? labels[0];
  };

  const optionRender = (option: (typeof options)[number]) => {
    const isSearch = "__rc_cascader_search_mark__" in option;

    if (!option.value.startsWith(CUSTOM_JOB_PREFIX)) {
      const aliases = option.aliases?.slice(0, 5);
      const hasMoreAliases = option.aliases?.length > 5;

      return (
        <Stack>
          <span
            className={classNames({
              "truncate": true,
              "max-w-sm": !isSearch,
              "max-w-lg": isSearch,
            })}
          >
            {extractLabel(option.label)}
          </span>
          {option.aliases?.length > 0 && (
            <span
              className={classNames({
                "whitespace-normal text-xs !font-normal text-gray-500": true,
                "max-w-sm": !isSearch,
                "max-w-lg": isSearch,
              })}
            >
              {aliases.map((alias, index) => (
                <>
                  {index > 0 && <span>, </span>}
                  <HighlightedText key={alias} search={search} text={alias} />
                </>
              ))}
              {hasMoreAliases && <span>, ...</span>}
            </span>
          )}
        </Stack>
      );
    }

    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
        <span className="max-w-xs truncate">{option.label}</span>
        <IconButton
          className="!p-1"
          onClick={(event) => {
            event.stopPropagation();
            setSelectedCustomJobForEdition(option.value);
          }}
        >
          <Edit className="-my-1 text-xs text-gray-500" />
        </IconButton>
      </Stack>
    );
  };

  const searchRender = (input: string, path: typeof options) => {
    const isBestMatch = path[0]?.value === BEST_MATCH_KEY;

    return path.map((option, index) => {
      const text = extractLabel(option.label) as string;

      return (
        <>
          {index > 0 && <span className="!text-gray-500"> › </span>}
          {isBestMatch && index === 0 ? text : <HighlightedText text={text} search={input} />}
        </>
      );
    });
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          borderRadius: 4,
          colorHighlight: theme.colors.secondary[500],
          colorBorder: theme.colors.gray[300],
          colorTextPlaceholder: theme.colors.gray[400],
          colorPrimary: theme.colors.primary[400],
          colorPrimaryHover: theme.colors.gray[400],
          boxShadowSecondary: "none",
          zIndexPopupBase: 9999,
        },
        components: {
          Select: {
            fontSize: 16,
          },
        },
      }}
    >
      {jobRequest && (
        <JobRequestModal
          isOpen={openJobRequestModal}
          onClose={async () => {
            setOpenJobRequestModal(false);
          }}
          externalJob={jobRequest.externalJob}
        />
      )}

      {enableCustomJobs && (
        <CustomJobCreationModal
          isOpen={openCustomJobCreationModal}
          onClose={async () => {
            setOpenCustomJobCreationModal(false);
            await refetchJobs();
          }}
        />
      )}

      {enableCustomJobs && selectedCustomJobForEdition && (
        <CustomJobEditionModal
          isOpen={!!selectedCustomJobForEdition}
          customJobId={selectedCustomJobForEdition}
          onClose={async () => {
            setSelectedCustomJobForEdition(null);
            await refetchJobs();
          }}
        />
      )}

      {multiple ? (
        <Cascader
          size={size}
          multiple
          disabled={disabled}
          className={className}
          showSearch={{ render: searchRender }}
          searchValue={search}
          onSearch={(value) => setSearch(value)}
          changeOnSelect
          tagRender={(option) => {
            const isGroup = !!options.find((o) => o.value === option.value);
            return (
              <Chip
                size="small"
                variant="outlined"
                className="mx-1 my-0.5"
                label={option.label}
                icon={isGroup ? <Workspaces className="!ml-1.5" fontSize="small" /> : undefined}
                onDelete={() => option.onClose()}
              />
            );
          }}
          optionRender={optionRender}
          displayRender={displayRender}
          dropdownRender={dropdownRender}
          value={values}
          options={options}
          onChange={onJobsChange}
          placeholder={dynamicPlaceholder}
          open={open}
          onDropdownVisibleChange={(open) => isOpen(open)}
        />
      ) : (
        <Cascader
          size={size}
          disabled={disabled}
          className={className}
          showSearch={{ render: searchRender }}
          searchValue={search}
          onSearch={(value) => setSearch(value)}
          displayRender={displayRender}
          dropdownRender={dropdownRender}
          optionRender={optionRender}
          value={values[0]}
          options={options}
          onChange={onJobChange}
          placeholder={dynamicPlaceholder}
          open={open}
          onDropdownVisibleChange={(open) => isOpen(open)}
        />
      )}
    </ConfigProvider>
  );
};

type CustomJobEditionModalProps = {
  isOpen: boolean;
  onClose: (editedCustomJob?: CustomJob) => void;
  customJobId: string;
};

type CustomJobEditionModalFormValues = {
  id: number | null;
  name: string | null;
  jobs: WeightedJob[];
  sharedWithCompany: boolean;
};

export const CustomJobEditionModal: React.FC<CustomJobEditionModalProps> = ({ isOpen, onClose, customJobId }) => {
  const { apiFetch } = useApi();
  const { user } = useSession();
  const [isDeletingCustomJob, setIsDeletingCustomJob] = useState(false);
  const [showCustomJobDeletionModal, setShowCustomJobDeletionModal] = useState(false);
  const isUserRecruiter = user?.permissions.role === UserRole.RECRUITER;

  const { data: jobsData } = useListJobsQuery({});
  const customJob = jobsData?.customJobs.find((job) => `${job.id}` === customJobId.slice(CUSTOM_JOB_PREFIX.length));

  const form = useForm<CustomJobEditionModalFormValues>({
    initialValues: {
      id: null,
      name: null,
      jobs: [],
      sharedWithCompany: false,
    },
    validationSchema: UpdateCustomJobSchema,
    onSubmit: async (values) => {
      const res = await apiFetch<UpdateCustomJobResponse>("/api/update-custom-job", {
        body: values,
        successMessage: `The ${values.name} job has been successfully updated`,
      });

      handleClose(res.customJob);
    },
  });

  useEffect(() => {
    if (!isOpen || !customJob) return;

    form.updateValues({
      id: customJob.id,
      name: customJob.name,
      jobs: customJob.items,
      sharedWithCompany: customJob.sharedWithCompany,
    });
  }, [customJob, isOpen]);

  const handleClose = (editedCustomJob?: CustomJob) => {
    onClose(editedCustomJob);

    form.resetForm();
  };

  const onWeightInputBlur = (changedJobIndex: number) => {
    if (form.values.jobs.length !== 2) {
      return;
    }

    const newJobs = [...form.values.jobs];
    const changedJob = newJobs[changedJobIndex] as WeightedJob;

    newJobs.forEach((item, index) => {
      if (index !== changedJobIndex) {
        item.weight = 100 - changedJob.weight;
      }
    });

    form.updateValues({ jobs: newJobs });
  };

  const weightsSum = sumBy(form.values.jobs, (item) => {
    return item.weight;
  });

  const onCustomJobDeletionConfirmation = async () => {
    if (!customJob) return;

    await apiFetch("/api/delete-custom-job", {
      body: { id: customJob.id },
      setIsLoading: setIsDeletingCustomJob,
      successMessage: `The ${customJob.name} job has been successfully deleted`,
    });

    handleClose();
  };

  return (
    <LegacyModal
      isOpen={isOpen}
      title="Edit custom job"
      onClose={() => {
        handleClose();
      }}
      className="w-[560px]"
    >
      <ConfirmationModal
        confirmLabel="Confirm"
        isLoading={isDeletingCustomJob}
        description={
          <Stack gap={3}>
            <Typography variant="h3">You're about to delete the custom job "{customJob?.name}".</Typography>

            <Stack>
              <Typography>This action cannot be undone.</Typography>
            </Stack>
          </Stack>
        }
        isOpen={showCustomJobDeletionModal}
        title="Confirm Custom Job Deletion"
        onClose={() => setShowCustomJobDeletionModal(false)}
        onConfirm={onCustomJobDeletionConfirmation}
      />

      <div className="mt-3 flex flex-col items-start">
        <form className="mt-4 w-full" onSubmit={form.handleSubmit}>
          <div className="flex">
            <LegacyFormControl className="flex-1" label="Figures Jobs">
              <Stack spacing={2}>
                {form.values.jobs.map((job, index) => (
                  <JobSelector key={index} className="w-full" jobIds={[`${job.job.id}`]} disabled onChange={noop} />
                ))}
              </Stack>
            </LegacyFormControl>

            {form.values.jobs.length > 1 && (
              <LegacyFormControl
                requiredHint
                className="ml-4"
                label="Weights"
                tooltip={
                  <div className="flex w-96 flex-col p-2">
                    <span>
                      Use weights to grant more importance to a specific job's market data into your combination.
                    </span>

                    <span className="mt-2">
                      For example, if your employee is spending 70% of their time doing Product Design and 30% Product
                      Management, you might want the weights to reflect this reality.
                    </span>
                  </div>
                }
              >
                <div className="flex flex-col space-y-1">
                  {form.values.jobs.map((item, index) => {
                    return (
                      <div key={item.job.id} className="flex items-center">
                        <TextInput
                          required
                          affix="%"
                          className="text-right"
                          max={100}
                          min={1}
                          type="number"
                          value={item.weight}
                          onBlur={() => {
                            onWeightInputBlur(index);
                          }}
                          onChange={(event) => {
                            const jobs = [...form.values.jobs];
                            const jobRow = jobs[index];

                            if (jobRow) {
                              jobRow.weight = parseInt(event.target.value);
                              form.updateValues({ jobs });
                            }
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </LegacyFormControl>
            )}
          </div>

          <LegacyFormControl requiredHint className="mt-8" label="Job name">
            <div className="flex">
              <TextInput
                required
                value={form.values.name ?? ""}
                onChange={(event) => {
                  form.updateValues({ name: event.target.value });
                }}
              />
            </div>
          </LegacyFormControl>

          {!isUserRecruiter && (
            <LegacyFormControl className="mt-8" label="Sharing">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.values.sharedWithCompany}
                    onChange={(event) => {
                      form.updateValues({ sharedWithCompany: event.target.checked });
                    }}
                  />
                }
                label={
                  <Typography>Share this job with members of {user?.company.name ?? "my organisation"}</Typography>
                }
              />
            </LegacyFormControl>
          )}

          <div className="mt-8 flex items-center justify-end border-t pt-3">
            {form.values.jobs.length > 0 && weightsSum !== 100 && (
              <LegacyText className="mr-4" icon="warning">
                The provided weights sum doesn't add up to 100%.
              </LegacyText>
            )}

            <Button
              variant="outlined"
              disabled={isDeletingCustomJob}
              color="error"
              className="mr-2"
              onClick={() => setShowCustomJobDeletionModal(true)}
            >
              Delete custom job
            </Button>

            <Button variant="contained" disabled={!form.isValid || form.isSubmitting} type="submit">
              Update custom job
            </Button>
          </div>
        </form>
      </div>
    </LegacyModal>
  );
};

type CustomJobCreationModalProps = {
  isOpen: boolean;
  onClose: (newCustomJob?: CustomJob) => void;
};

type CustomJobCreationModalFormValues = {
  name: string;
  jobs: WeightedJob[];
  sharedWithCompany: boolean;
};

export const CustomJobCreationModal: React.FC<CustomJobCreationModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { apiFetch } = useApi();
  const { user } = useSession();
  const isUserRecruiter = user?.permissions.role === UserRole.RECRUITER;

  const { data: jobsData } = useListJobsQuery();

  const form = useForm<CustomJobCreationModalFormValues>({
    initialValues: {
      name: "",
      jobs: [],
      sharedWithCompany: false,
    },
    validationSchema: CreateCustomJobSchema,
    onSubmit: async (values) => {
      const res = await apiFetch<CreateCustomJobResponse>("/api/create-custom-job", {
        body: {
          name: values.name,
          jobs: values.jobs,
          sharedWithCompany: values.sharedWithCompany,
        },
        successMessage: t("components.modals.custom-job-creation-modal.job-has-successfully-been-created", {
          name: values.name,
        }),
      });

      handleClose(res.customJob);
    },
  });

  const handleClose = (newCustomJob?: CustomJob) => {
    onClose(newCustomJob);

    form.resetForm();
  };

  const onWeightInputBlur = (changedJobIndex: number) => {
    if (form.values.jobs.length !== 2) {
      return;
    }

    const newJobs = [...form.values.jobs];
    const changedJob = newJobs[changedJobIndex] as WeightedJob;

    newJobs.forEach((item, index) => {
      if (index !== changedJobIndex) {
        item.weight = 100 - changedJob.weight;
      }
    });

    form.updateValues({ jobs: newJobs });
  };

  // Recompute weights when the number of jobs change
  useEffect(() => {
    form.updateValues({
      jobs: form.values.jobs.map((item) => {
        return {
          job: item.job,
          weight: Math.floor(100 / form.values.jobs.length),
        };
      }),
    });
  }, [form.values.jobs.length]);

  const jobNamePlaceholder = form.values.jobs
    .map((item) => {
      return item.job.name;
    })
    .join(" × ");

  const weightsSum = sumBy(form.values.jobs, (item) => {
    return item.weight;
  });

  return (
    <LegacyModal
      isOpen={isOpen}
      title={t("components.modals.custom-job-creation-modal.new-custom-job")}
      onClose={() => {
        handleClose();
      }}
      className="w-[560px]"
    >
      <div className="mt-3 flex flex-col items-start">
        <LegacyText className="max-w-lg" icon="information">
          {t("components.modals.custom-job-creation-modal.combine-multiple-jobs")}
        </LegacyText>

        <form className="mt-4 w-full" onSubmit={form.handleSubmit}>
          <div className="mt-4 flex">
            <LegacyFormControl
              requiredHint
              className="flex-1"
              label={t("components.modals.custom-job-creation-modal.figures-jobs")}
            >
              <Stack spacing={2}>
                {form.values.jobs.map((job, index) => (
                  <JobSelector
                    key={index}
                    className="w-full"
                    jobIds={[`${job.job.id}`]}
                    onChange={([jobId]) => {
                      const newJobIds = [...form.values.jobs.map((item) => `${item.job.id}`)];
                      if (jobId) {
                        newJobIds[index] = jobId;
                      } else {
                        newJobIds.splice(index, 1);
                      }

                      const newJobs = newJobIds.map((jobId, index) => {
                        const job = jobsData?.jobs.find((job) => `${job.id}` === jobId);
                        if (!job) return null;

                        return { job, weight: form.values.jobs[index]?.weight ?? 100 };
                      });

                      form.updateValues({ jobs: compact(newJobs) });
                    }}
                  />
                ))}
                <JobSelector
                  key={form.values.jobs.length}
                  className="w-full"
                  jobIds={[]}
                  placeholder={
                    form.values.jobs.length > 0
                      ? t("components.modals.custom-job-creation-modal.select-more-jobs-to-combine")
                      : t("components.modals.custom-job-creation-modal.select-at-least-2-jobs")
                  }
                  onChange={([jobId]) => {
                    if (!jobId) return;

                    const newJobIds = [...form.values.jobs.map((item) => `${item.job.id}`), jobId];
                    const newJobs = newJobIds.map((jobId, index) => {
                      const job = jobsData?.jobs.find((job) => `${job.id}` === jobId);
                      if (!job) return null;

                      return { job, weight: form.values.jobs[index]?.weight ?? 100 };
                    });

                    form.updateValues({ jobs: compact(newJobs) });
                  }}
                />
              </Stack>
            </LegacyFormControl>

            {form.values.jobs.length > 1 && (
              <LegacyFormControl
                requiredHint
                className="ml-4"
                label={t("components.modals.custom-job-creation-modal.weights")}
                tooltip={
                  <div className="flex w-96 flex-col p-2">
                    <span>{t("components.modals.custom-job-creation-modal.use-weights")}</span>

                    <span className="mt-2">{t("components.modals.custom-job-creation-modal.for-example")}</span>
                  </div>
                }
              >
                <div className="flex flex-col space-y-1">
                  {form.values.jobs.map((item, index) => {
                    return (
                      <div key={item.job.id} className="flex items-center">
                        <TextInput
                          required
                          affix="%"
                          className="text-right"
                          max={100}
                          min={1}
                          type="number"
                          value={item.weight}
                          onBlur={() => {
                            onWeightInputBlur(index);
                          }}
                          onChange={(event) => {
                            const jobs = [...form.values.jobs];
                            const jobRow = jobs[index];

                            if (jobRow) {
                              jobRow.weight = parseInt(event.target.value);
                              form.updateValues({ jobs });
                            }
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </LegacyFormControl>
            )}
          </div>

          <LegacyFormControl
            requiredHint
            className="mt-8"
            label={t("components.modals.custom-job-creation-modal.job-name")}
          >
            <div className="flex">
              <TextInput
                required
                placeholder={jobNamePlaceholder}
                value={form.values.name}
                onChange={(event) => {
                  form.updateValues({ name: event.target.value });
                }}
              />
              {!form.values.name && form.values.jobs?.length >= 1 && (
                <Button
                  variant="outlined"
                  className="ml-2"
                  onClick={() => {
                    form.updateValues({ name: jobNamePlaceholder });
                  }}
                  startIcon={<Check className="ml-2.5" />}
                />
              )}
            </div>
          </LegacyFormControl>

          {!isUserRecruiter && (
            <LegacyFormControl className="mt-8" label={t("components.modals.custom-job-creation-modal.sharing")}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={form.values.sharedWithCompany}
                    onChange={(event) => {
                      form.updateValues({ sharedWithCompany: event.target.checked });
                    }}
                  />
                }
                label={
                  <Typography>
                    {t("components.modals.custom-job-creation-modal.share-this-job", {
                      companyName: user?.company.name ?? "my organisation",
                    })}
                  </Typography>
                }
              />
            </LegacyFormControl>
          )}

          <div className="mt-8 flex items-center justify-end border-t pt-3">
            {form.values.jobs.length > 0 && weightsSum !== 100 && (
              <LegacyText className="mr-4" icon="warning">
                {t("components.modals.custom-job-creation-modal.sum-doesnt-add-up")}
              </LegacyText>
            )}

            <Button variant="contained" disabled={!form.isValid || form.isSubmitting} type="submit">
              {t("components.modals.custom-job-creation-modal.create-custom-job")}
            </Button>
          </div>
        </form>
      </div>
    </LegacyModal>
  );
};
